import React, { useEffect, useState, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import Layout from "../components/Layout";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { toast, Toaster } from "sonner";
import { Input } from "../components/ui/input";
import { MessageCircle, CheckCircle2, CalendarClock, AlertCircle, Phone, ArrowRight, Bell, Settings, Send, Clock, Syringe, Pencil, Trash2 } from "lucide-react";
import { CLINIC } from "../lib/clinic";
import { openWhatsApp } from "../lib/whatsapp";
import { RECALL_TONES, getRecallTone, setRecallTone, buildVaccineRecallMsg } from "../lib/recallTones";

const TABS = [
  { key: "today", label: "Due today", icon: CalendarClock, tone: "text-pink-700" },
  { key: "overdue", label: "Overdue", icon: AlertCircle, tone: "text-red-600" },
  { key: "upcoming", label: "Upcoming (7d)", icon: Bell, tone: "text-emerald-700" },
  { key: "all", label: "All", icon: Bell, tone: "text-slate-600" },
  { key: "vaccines", label: "Vaccines", icon: Syringe, tone: "text-sky-700" },
];

const DAY_NAMES = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

function daysLabel(n) {
  if (n == null) return "";
  if (n === 0) return "Today";
  if (n < 0) return `${Math.abs(n)}d overdue`;
  if (n === 1) return "Tomorrow";
  return `in ${n} days`;
}

function nextDispatchDate(dow, hour, minute) {
  const now = new Date();
  const d = new Date(now);
  const daysAhead = (dow - now.getDay() + 7) % 7;
  d.setDate(now.getDate() + daysAhead);
  d.setHours(hour, minute, 0, 0);
  if (d <= now) d.setDate(d.getDate() + 7);
  return d;
}
function relative(diffMs) {
  const mins = Math.round(diffMs / 60000);
  if (mins < 60) return `in ${mins} min`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `in ${hrs} hr`;
  const days = Math.round(hrs / 24);
  return `in ${days} day${days === 1 ? "" : "s"}`;
}

export default function Reminders() {
  const [tab, setTab] = useState("today");
  const [rows, setRows] = useState([]);
  const [vaccineRows, setVaccineRows] = useState([]);
  const [vaccineWindow, setVaccineWindow] = useState(30);
  const [counts, setCounts] = useState({ today: 0, overdue: 0, upcoming: 0 });
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [batchRunning, setBatchRunning] = useState(false);
  const [batchProgress, setBatchProgress] = useState({ done: 0, total: 0 });
  const cancelRef = useRef(false);

  const load = useCallback(async (t) => {
    setLoading(true);
    try {
      if (t === "vaccines") {
        const [v, c, s] = await Promise.all([
          api.get(`/reminders/vaccines?window_days=${vaccineWindow}`),
          api.get("/reminders/counts"),
          api.get("/settings/reminders"),
        ]);
        setVaccineRows(v.data.reminders || []);
        setCounts(c.data || {});
        setSettings(s.data);
      } else {
        const [r, c, s] = await Promise.all([
          api.get(`/reminders?window=${t}`),
          api.get("/reminders/counts"),
          api.get("/settings/reminders"),
        ]);
        setRows(r.data.reminders || []);
        setCounts(c.data || {});
        setSettings(s.data);
      }
    } catch {
      toast.error("Failed to load reminders");
    }
    setLoading(false);
  }, [vaccineWindow]);

  useEffect(() => { load(tab); }, [tab, load]);

  const saveSettings = async (draft) => {
    try {
      const r = await api.put("/settings/reminders", draft);
      setSettings(r.data);
      toast.success("Schedule saved");
      setSettingsOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    }
  };

  const sendWhatsapp = async (row) => {
    const dateStr = row.follow_up_date ? new Date(row.follow_up_date).toLocaleDateString(undefined, { day: "2-digit", month: "long", year: "numeric" }) : "";
    const overdue = row.days_until < 0;
    const msg = overdue
      ? `Dear ${row.patient_name},

A gentle reminder from ${CLINIC.name} — your follow-up visit was due on ${dateStr} (${Math.abs(row.days_until)} day${Math.abs(row.days_until) === 1 ? "" : "s"} ago).
${row.next_investigations ? `Please carry these reports: ${row.next_investigations}\n` : ""}
Timely review keeps your treatment on track. Reply to this message to book a convenient time.`
      : `Dear ${row.patient_name},

This is a follow-up reminder from ${CLINIC.name}.
Your next scheduled visit is on ${dateStr}.
${row.next_investigations ? `Please carry these reports: ${row.next_investigations}\n` : ""}
Reply to this message to confirm or reschedule.`;
    openWhatsApp(row.mobile, msg, overdue ? "followup_nudge" : "followup_reminder");
    try {
      await api.post(`/reminders/${row.visit_id}/sent`, { channel: "whatsapp" });
      setRows((prev) => prev.map((r) => r.visit_id === row.visit_id ? { ...r, reminded: true } : r));
      toast.success("Reminder marked as sent");
    } catch {
      toast.error("Could not update reminder status");
    }
  };

  const [editRow, setEditRow] = useState(null);

  const deleteReminder = async (row) => {
    if (!confirm(`Remove the follow-up reminder for ${row.patient_name}? The visit record is kept.`)) return;
    try {
      await api.delete(`/reminders/${row.visit_id}`);
      setRows((prev) => prev.filter((r) => r.visit_id !== row.visit_id));
      toast.success("Reminder removed");
      load(tab);
    } catch {
      toast.error("Failed to remove reminder");
    }
  };

  const saveReminderEdit = async (draft) => {
    try {
      await api.put(`/reminders/${draft.visit_id}`, {
        follow_up_date: draft.follow_up_date,
        next_investigations: draft.next_investigations,
      });
      toast.success("Reminder updated");
      setEditRow(null);
      load(tab);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update reminder");
    }
  };

  const markSent = async (row) => {
    try {
      await api.post(`/reminders/${row.visit_id}/sent`, { channel: "manual" });
      setRows((prev) => prev.map((r) => r.visit_id === row.visit_id ? { ...r, reminded: true } : r));
      toast.success("Marked as reminded");
    } catch {
      toast.error("Failed");
    }
  };

  const batchSend = async (onlyOverdue = false) => {
    // Fetch due today + overdue (union) that are not yet reminded
    setBatchRunning(true);
    cancelRef.current = false;
    try {
      const [t, o] = await Promise.all([
        onlyOverdue ? Promise.resolve({ data: { reminders: [] } }) : api.get("/reminders?window=today"),
        api.get("/reminders?window=overdue"),
      ]);
      const merged = [...(t.data.reminders || []), ...(o.data.reminders || [])]
        .filter((r) => !r.reminded && r.mobile);
      setBatchProgress({ done: 0, total: merged.length });
      if (merged.length === 0) {
        toast.info(onlyOverdue ? "No overdue patients pending a nudge — all reminded." : "Nothing to send — everyone is already reminded.");
        setBatchRunning(false);
        return;
      }
      const sentIds = [];
      for (let i = 0; i < merged.length; i++) {
        if (cancelRef.current) break;
        const row = merged[i];
        const dateStr = row.follow_up_date ? new Date(row.follow_up_date).toLocaleDateString(undefined, { day: "2-digit", month: "long" }) : "";
        const overdue = row.days_until < 0;
        const msg = overdue
          ? `Dear ${row.patient_name}, a gentle reminder from ${CLINIC.name} — your follow-up visit was due on ${dateStr} (${Math.abs(row.days_until)} day${Math.abs(row.days_until) === 1 ? "" : "s"} ago).${row.next_investigations ? " Please carry: " + row.next_investigations + "." : ""} Timely review keeps your treatment on track. Reply to book a time.`
          : `Dear ${row.patient_name}, this is a follow-up reminder from ${CLINIC.name}. Your next scheduled visit is on ${dateStr}.${row.next_investigations ? " Please carry: " + row.next_investigations + "." : ""} Reply to confirm.`;
        openWhatsApp(row.mobile, msg, overdue ? "followup_nudge" : "followup_reminder");
        sentIds.push(row.visit_id);
        setBatchProgress({ done: i + 1, total: merged.length });
        // brief stagger so the popup blocker + tab-open doesn't overwhelm the browser
        await new Promise((res) => setTimeout(res, 1500));
      }
      if (sentIds.length) {
        await api.post("/reminders/batch-sent", { visit_ids: sentIds, channel: "whatsapp" });
        // reload
        load(tab);
        toast.success(`${sentIds.length} reminder tab(s) opened and marked sent`);
      }
    } catch (e) {
      toast.error("Batch send failed");
    }
    setBatchRunning(false);
  };

  const dispatchInfo = settings && settings.enabled ? (() => {
    const d = nextDispatchDate(settings.day_of_week, settings.hour, settings.minute);
    const diff = d.getTime() - Date.now();
    return {
      label: `${DAY_NAMES[settings.day_of_week]}, ${String(settings.hour).padStart(2, "0")}:${String(settings.minute).padStart(2, "0")}`,
      rel: relative(diff),
      isNow: diff < 60 * 60 * 1000, // within 1 hour
    };
  })() : null;

  return (
    <Layout>
      <Toaster position="top-right" richColors />
      <div className="flex items-start justify-between">
        <div>
          <div className="overline">CONTINUITY OF CARE</div>
          <h1 className="font-heading text-3xl font-semibold mt-1 text-slate-900 flex items-center gap-3">
            <Bell size={26} className="text-pink-600" />
            Follow-up reminders
          </h1>
          <p className="mt-2 text-sm text-slate-600 max-w-xl">
            Automatic list of patients whose follow-up is due. Send a pre-filled WhatsApp reminder in one click —
            the system tracks who has already been contacted so no patient is missed and none is contacted twice.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={() => setSettingsOpen(true)}
            className="gap-2 border-slate-300"
            data-testid="reminder-settings-btn"
          >
            <Settings size={14} /> Schedule
          </Button>
          <Button
            onClick={() => batchSend(true)}
            disabled={batchRunning || counts.overdue === 0}
            className="bg-amber-500 hover:bg-amber-600 text-white gap-2"
            title="One-tap WhatsApp nudge to every overdue patient not yet reminded — 'your follow-up date has passed' wording"
            data-testid="nudge-overdue-btn"
          >
            <AlertCircle size={14} />
            {batchRunning ? `Sending ${batchProgress.done}/${batchProgress.total}…` : `Nudge all overdue${counts.overdue ? ` (${counts.overdue})` : ""}`}
          </Button>
          <Button
            onClick={() => batchSend()}
            disabled={batchRunning || counts.today + counts.overdue === 0}
            className="bg-pink-600 hover:bg-pink-700 text-white gap-2"
            data-testid="batch-send-btn"
          >
            <Send size={14} />
            {batchRunning ? `Sending ${batchProgress.done}/${batchProgress.total}…` : "Batch send all due"}
          </Button>
        </div>
      </div>

      {/* Schedule strip */}
      {dispatchInfo && (
        <div className={`mt-4 rounded-md px-4 py-2.5 text-sm flex items-center justify-between border ${dispatchInfo.isNow ? "bg-pink-50 border-pink-200 text-pink-800" : "bg-slate-50 border-slate-200 text-slate-700"}`} data-testid="dispatch-strip">
          <div className="flex items-center gap-2">
            <Clock size={14} className={dispatchInfo.isNow ? "text-pink-600" : "text-slate-500"} />
            <span>
              Next dispatch slot: <span className="font-medium">{dispatchInfo.label}</span>
              <span className="text-xs text-slate-500 ml-2">({dispatchInfo.rel})</span>
            </span>
          </div>
          {dispatchInfo.isNow && counts.today + counts.overdue > 0 && (
            <span className="text-xs font-semibold uppercase tracking-wider text-pink-700">Ready to send</span>
          )}
        </div>
      )}
      {settings && !settings.enabled && (
        <div className="mt-4 rounded-md px-4 py-2.5 text-sm border alert-warning" data-testid="schedule-disabled">
          Scheduled dispatch is <strong>off</strong>. Send manually or turn it on in Schedule settings.
        </div>
      )}

      {/* Stat cards */}
      <div className="grid grid-cols-3 gap-4 mt-6">
        <StatCard label="Due today" value={counts.today} tone="indigo" testid="stat-today" />
        <StatCard label="Overdue" value={counts.overdue} tone="red" testid="stat-overdue" />
        <StatCard label="Upcoming (7 days)" value={counts.upcoming} tone="emerald" testid="stat-upcoming" />
      </div>

      {/* Tabs */}
      <div className="mt-6 flex gap-1 border-b border-slate-200">
        {TABS.map((t) => {
          const Icon = t.icon;
          const active = tab === t.key;
          return (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              data-testid={`tab-${t.key}`}
              className={`px-4 py-2 -mb-px text-sm flex items-center gap-2 border-b-2 ${
                active ? "border-pink-600 text-pink-700 font-medium" : "border-transparent text-slate-500 hover:text-slate-800"
              }`}
            >
              <Icon size={14} className={active ? "text-pink-600" : ""} /> {t.label}
            </button>
          );
        })}
      </div>

      {/* List */}
      <div className="mt-4 space-y-2" data-testid="reminders-list">
        {loading && <div className="text-sm text-slate-500 p-4">Loading…</div>}
        {!loading && tab === "vaccines" ? (
          <VaccineRemindersList
            rows={vaccineRows}
            windowDays={vaccineWindow}
            onWindowChange={(w) => setVaccineWindow(w)}
          />
        ) : (
          <>
            {!loading && rows.length === 0 && (
              <div className="border border-dashed border-slate-300 rounded-md p-12 text-center bg-white">
                <Bell size={26} className="text-slate-300 mx-auto" />
                <div className="mt-3 text-sm font-medium text-slate-700">No {TABS.find((t) => t.key === tab)?.label.toLowerCase()} reminders</div>
                <div className="mt-1 text-xs text-slate-500">Follow-ups you schedule while saving a visit will appear here.</div>
              </div>
            )}
            {rows.map((r) => (
          <div
            key={r.visit_id}
            className="bg-white border border-slate-200 rounded-md p-4 flex items-center justify-between gap-4 hover:border-pink-300 transition-colors"
            data-testid={`reminder-row-${r.visit_id}`}
          >
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <Link to={`/patient/${r.patient_id}`} className="font-medium text-slate-900 hover:text-pink-700">
                  {r.patient_name || "Unknown"}
                </Link>
                <span className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold tabular">{r.patient_id}</span>
                {r.reminded && (
                  <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-1.5 py-0.5" data-testid={`reminded-badge-${r.visit_id}`}>
                    <CheckCircle2 size={10} /> Reminded
                  </span>
                )}
              </div>
              <div className="text-xs text-slate-500 flex items-center gap-3 mt-1">
                {r.mobile && <span className="flex items-center gap-1 tabular"><Phone size={11} /> {r.mobile}</span>}
                {r.diagnosis && <span className="truncate">Dx: {r.diagnosis}</span>}
              </div>
              <div className="mt-1 flex items-center gap-3 text-xs">
                <span className="tabular text-slate-700 font-medium">
                  {r.follow_up_date ? new Date(r.follow_up_date).toLocaleDateString(undefined, { day: "2-digit", month: "short" }) : "—"}
                </span>
                <span className={`text-[11px] font-semibold uppercase tracking-wider ${r.days_until < 0 ? "text-red-600" : r.days_until === 0 ? "text-pink-700" : "text-emerald-700"}`}>
                  {daysLabel(r.days_until)}
                </span>
                {r.next_investigations && <span className="text-slate-500 truncate">· Bring: {r.next_investigations}</span>}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                onClick={() => sendWhatsapp(r)}
                className={`${r.days_until < 0 ? "bg-amber-500 hover:bg-amber-600" : "bg-emerald-600 hover:bg-emerald-700"} text-white h-9 gap-2`}
                title={r.days_until < 0 ? "One-tap overdue nudge — 'your follow-up date has passed' wording" : "Send WhatsApp follow-up reminder"}
                data-testid={`whatsapp-remind-${r.visit_id}`}
              >
                <MessageCircle size={14} /> {r.days_until < 0 ? "Nudge" : "WhatsApp"}
              </Button>
              {!r.reminded && (
                <button
                  onClick={() => markSent(r)}
                  className="text-xs text-slate-500 hover:text-slate-800 px-2 py-1"
                  data-testid={`mark-sent-${r.visit_id}`}
                >
                  Mark sent
                </button>
              )}
              <button
                onClick={() => setEditRow(r)}
                className="p-2 rounded hover:bg-slate-100 text-slate-500"
                title="Edit reminder"
                data-testid={`edit-reminder-${r.visit_id}`}
              >
                <Pencil size={14} />
              </button>
              <button
                onClick={() => deleteReminder(r)}
                className="p-2 rounded hover:bg-red-50 text-red-500"
                title="Remove reminder"
                data-testid={`delete-reminder-${r.visit_id}`}
              >
                <Trash2 size={14} />
              </button>
              <Link
                to={`/patient/${r.patient_id}`}
                className="p-2 rounded hover:bg-slate-100 text-slate-500"
                title="Open patient"
                data-testid={`open-patient-${r.visit_id}`}
              >
                <ArrowRight size={14} />
              </Link>
            </div>
          </div>
        ))}
          </>
        )}
      </div>

      <ScheduleDialog
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        settings={settings}
        onSave={saveSettings}
      />
      {editRow && (
        <EditReminderDialog
          row={editRow}
          onClose={() => setEditRow(null)}
          onSave={saveReminderEdit}
        />
      )}
    </Layout>
  );
}

function ScheduleDialog({ open, onClose, settings, onSave }) {
  const [d, setD] = useState(settings || { day_of_week: 1, hour: 10, minute: 0, timezone: "Asia/Kolkata", enabled: true });
  useEffect(() => { if (settings) setD(settings); }, [settings]);
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <Clock size={16} /> Reminder dispatch schedule
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <p className="text-xs text-slate-500 leading-relaxed">
            Pick the day of the week and time when you'd like to send follow-up reminders in a single batch.
            You can still send individual reminders any time.
          </p>
          <div className="flex items-center justify-between border border-slate-200 rounded-md p-3">
            <div>
              <div className="text-sm font-medium">Scheduled dispatch</div>
              <div className="text-xs text-slate-500">Turn off to send only manually.</div>
            </div>
            <Switch
              checked={!!d.enabled}
              onCheckedChange={(v) => setD({ ...d, enabled: v })}
              data-testid="schedule-enabled-switch"
            />
          </div>
          <div className={d.enabled ? "" : "opacity-50 pointer-events-none"}>
            <Label className="overline">DAY OF WEEK</Label>
            <div className="mt-1 grid grid-cols-7 gap-1">
              {DAY_NAMES.map((name, i) => (
                <button
                  key={name}
                  onClick={() => setD({ ...d, day_of_week: i })}
                  className={`py-2 text-xs rounded font-medium border ${d.day_of_week === i ? "bg-pink-600 text-white border-pink-600" : "bg-white border-slate-200 hover:bg-slate-50"}`}
                  data-testid={`day-${i}`}
                >
                  {name.slice(0, 3)}
                </button>
              ))}
            </div>
            <div className="grid grid-cols-2 gap-3 mt-4">
              <div>
                <Label className="overline">HOUR</Label>
                <select
                  value={d.hour}
                  onChange={(e) => setD({ ...d, hour: parseInt(e.target.value) })}
                  className="mt-1 h-9 w-full border border-slate-200 rounded px-2 text-sm tabular"
                  data-testid="schedule-hour"
                >
                  {Array.from({ length: 24 }, (_, i) => i).map((h) => (
                    <option key={h} value={h}>{String(h).padStart(2, "0")}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label className="overline">MINUTE</Label>
                <select
                  value={d.minute}
                  onChange={(e) => setD({ ...d, minute: parseInt(e.target.value) })}
                  className="mt-1 h-9 w-full border border-slate-200 rounded px-2 text-sm tabular"
                  data-testid="schedule-minute"
                >
                  {[0, 15, 30, 45].map((m) => (
                    <option key={m} value={m}>{String(m).padStart(2, "0")}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="mt-3">
              <Label className="overline">TIMEZONE</Label>
              <select
                value={d.timezone || "Asia/Kolkata"}
                onChange={(e) => setD({ ...d, timezone: e.target.value })}
                className="mt-1 h-9 w-full border border-slate-200 rounded px-2 text-sm"
                data-testid="schedule-tz"
              >
                {["Asia/Kolkata", "Asia/Dubai", "Asia/Singapore", "Europe/London", "America/New_York", "UTC"].map((tz) => (
                  <option key={tz} value={tz}>{tz}</option>
                ))}
              </select>
            </div>
            <div className="mt-3 text-xs text-slate-500 tabular">
              Next dispatch:{" "}
              <span className="font-medium text-slate-800">
                {DAY_NAMES[d.day_of_week]}, {String(d.hour).padStart(2, "0")}:{String(d.minute).padStart(2, "0")}
              </span>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            onClick={() => onSave(d)}
            className="bg-pink-600 hover:bg-pink-700 text-white"
            data-testid="save-schedule-btn"
          >
            Save schedule
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EditReminderDialog({ row, onClose, onSave }) {
  const [d, setD] = useState({
    visit_id: row.visit_id,
    follow_up_date: row.follow_up_date ? row.follow_up_date.slice(0, 10) : "",
    next_investigations: row.next_investigations || "",
  });
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <Pencil size={15} /> Edit reminder — {row.patient_name}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label className="overline">FOLLOW-UP DATE</Label>
            <Input
              type="date"
              value={d.follow_up_date}
              onChange={(e) => setD({ ...d, follow_up_date: e.target.value })}
              className="mt-1 tabular"
              data-testid="edit-reminder-date"
            />
            <p className="text-[11px] text-slate-500 mt-1">Changing the date lets the patient be reminded again.</p>
          </div>
          <div>
            <Label className="overline">TESTS TO BRING</Label>
            <textarea
              value={d.next_investigations}
              onChange={(e) => setD({ ...d, next_investigations: e.target.value })}
              rows={2}
              placeholder="e.g. FBS, PPBS, HbA1c"
              className="mt-1 w-full border border-slate-200 rounded-md px-3 py-2 text-sm resize-y"
              data-testid="edit-reminder-tests"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            onClick={() => onSave(d)}
            disabled={!d.follow_up_date}
            className="bg-pink-600 hover:bg-pink-700 text-white"
            data-testid="edit-reminder-save"
          >
            Save changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function StatCard({ label, value, tone, testid }) {
  const tones = {
    indigo: "text-pink-700",
    red: "text-red-600",
    emerald: "text-emerald-700",
  };
  return (
    <div className="bg-white border border-slate-200 rounded-md p-4" data-testid={testid}>
      <div className="overline">{label}</div>
      <div className={`font-heading text-3xl font-semibold mt-1 tabular ${tones[tone] || "text-slate-700"}`}>
        {value ?? 0}
      </div>
    </div>
  );
}


// -------- Vaccine reminders tab --------
function VaccineRemindersList({ rows, windowDays, onWindowChange }) {
  const [tone, setTone] = useState(getRecallTone());
  const pickTone = (t) => { setTone(t); setRecallTone(t); };
  const send = (r) => {
    const dt = new Date(r.due_date).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
    const msg = buildVaccineRecallMsg(tone, {
      name: r.name,
      vaccine: r.vaccine,
      dateStr: dt,
      daysUntil: r.days_until,
    });
    openWhatsApp(r.mobile, msg);
    toast.success(`WhatsApp opened for ${r.name}`);
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <div className="text-xs text-slate-600">
          Showing due-window: <strong>next {windowDays} days</strong> · {rows.length} reminder{rows.length === 1 ? "" : "s"}
        </div>
        <div className="flex items-center gap-2">
          <div className="inline-flex rounded-md border border-slate-200 overflow-hidden text-[10px]" data-testid="vax-tone-toggle">
            {RECALL_TONES.map((t) => (
              <button
                key={t.key}
                onClick={() => pickTone(t.key)}
                title={t.hint}
                className={`px-2 py-0.5 ${tone === t.key ? "bg-slate-800 text-white" : "bg-white text-slate-500 hover:bg-slate-50"}`}
                data-testid={`vax-tone-${t.key}`}
              >
                {t.label}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-1 text-[11px]">
          {[14, 30, 60, 90].map((n) => (
            <button
              key={n}
              onClick={() => onWindowChange(n)}
              data-testid={`vax-window-${n}`}
              className={`px-2 py-0.5 rounded border ${windowDays === n ? "bg-sky-100 text-sky-800 border-sky-300" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"}`}
            >
              {n}d
            </button>
          ))}
          </div>
        </div>
      </div>

      {rows.length === 0 && (
        <div className="border border-dashed border-slate-300 rounded-md p-10 text-center bg-white" data-testid="vax-empty">
          <Syringe size={26} className="text-slate-300 mx-auto" />
          <div className="mt-3 text-sm font-medium text-slate-700">No vaccines due in this window</div>
          <div className="mt-1 text-xs text-slate-500">Widen the window or wait — reminders repopulate as birthdays and annual triggers approach.</div>
        </div>
      )}

      <div className="space-y-2">
        {rows.map((r, i) => (
          <div
            key={`${r.patient_id}-${r.vaccine}-${i}`}
            className="bg-white border border-slate-200 rounded-md p-4 flex items-center justify-between gap-4 hover:border-sky-300 transition-colors"
            data-testid={`vaccine-row-${r.patient_id}-${i}`}
          >
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <Link to={`/patient/${r.patient_id}`} className="font-medium text-slate-900 hover:text-sky-700">
                  {r.name || "Unknown"}
                </Link>
                <span className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold tabular">{r.patient_id}</span>
                <span className={`inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider rounded px-1.5 py-0.5 border ${r.schedule_type === "adult" ? "bg-pink-50 text-pink-700 border-pink-200" : "bg-sky-50 text-sky-700 border-sky-200"}`}>
                  {r.schedule_type} · age {r.age_years ?? "—"}
                </span>
              </div>
              <div className="mt-1 text-xs flex items-center gap-3">
                <span className="flex items-center gap-1 text-slate-700"><Syringe size={11} /> <strong>{r.vaccine}</strong></span>
                <span className="text-slate-400">·</span>
                <span className="text-slate-500">{r.band}</span>
              </div>
              <div className="mt-1 flex items-center gap-3 text-xs">
                {r.mobile && <span className="flex items-center gap-1 tabular text-slate-600"><Phone size={11} /> {r.mobile}</span>}
                <span className="tabular text-slate-700 font-medium">
                  {new Date(r.due_date).toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" })}
                </span>
                <span className={`text-[11px] font-semibold uppercase tracking-wider ${r.days_until < 0 ? "text-red-600" : r.days_until === 0 ? "text-pink-700" : "text-emerald-700"}`}>
                  {daysLabel(r.days_until)}
                </span>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                onClick={() => send(r)}
                className="bg-emerald-600 hover:bg-emerald-700 text-white h-9 gap-2"
                disabled={!r.mobile}
                data-testid={`vax-whatsapp-${r.patient_id}-${i}`}
              >
                <MessageCircle size={14} /> WhatsApp
              </Button>
              <Link
                to={`/patient/${r.patient_id}`}
                className="p-2 rounded hover:bg-slate-100 text-slate-500"
                title="Open patient"
                data-testid={`vax-open-${r.patient_id}-${i}`}
              >
                <ArrowRight size={14} />
              </Link>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
